import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Factory, Users, ClipboardList, TrendingUp, FileCheck2, Package, ShieldAlert, ArrowUpRight } from "lucide-react";
import TrendVsYesterday from "@/components/TrendVsYesterday";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, BarChart, Bar, Legend } from "recharts";
import StatusChip from "@/components/StatusChip";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

function Kpi({ label, value, sub, icon: Icon, tone = "brand", testid, prev, trendTestid, prevLabel }) {
  const tones = {
    brand: "bg-rose-50 text-brand",
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <Card data-testid={testid} className="p-5 border-slate-200 rounded-md hover:-translate-y-0.5 transition-transform duration-200 bg-white">
      <div className="flex items-start justify-between">
        <div className={`h-10 w-10 rounded-md flex items-center justify-center ${tones[tone]}`}>
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
        <span className="text-[11px] text-slate-400 font-mono tracking-wider">TODAY</span>
      </div>
      <div className="mt-4">
        <div className="text-[12.5px] text-slate-500 font-medium uppercase tracking-wide">{label}</div>
        <div className="mt-1 font-heading font-bold text-3xl text-slate-900 tracking-tight">{value}</div>
        <TrendVsYesterday today={value} yesterday={prev} label={prevLabel} testid={trendTestid} />
        {sub && <div className="mt-1 text-[12px] text-slate-500">{sub}</div>}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [bulletins, setBulletins] = useState([]);
  const nav = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    let m = true;
    (async () => {
      try {
        const [s, e, b] = await Promise.all([
          api.get("/dashboard/summary"),
          api.get("/enquiries"),
          api.get("/bulletins"),
        ]);
        if (!m) return;
        setData(s.data);
        setEnquiries(e.data.slice(0, 5));
        setBulletins(b.data.slice(0, 5));
      } catch {}
    })();
    return () => { m = false; };
  }, []);

  if (!data) {
    return <div className="text-slate-500 text-sm">Loading dashboard…</div>;
  }
  const k = data.kpis;
  const ky = data.kpis_yesterday || {};
  const cmpLabel = data.comparison?.is_yesterday === false ? data.comparison.label : "yesterday";

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11.5px] font-mono tracking-widest text-brand uppercase">Overview · {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
          <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight">Good day, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-slate-500 text-[14px] mt-1.5">Here's what's happening across your plant operations today.</p>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Kpi testid="kpi-active-lines" label="Active Lines" value={k.active_lines} sub="Reporting today" icon={Factory} tone="brand" prev={ky.active_lines} prevLabel={cmpLabel} trendTestid="kpi-trend-active-lines" />
        <Kpi testid="kpi-stitchers" label="Machine Operators" value={k.stitchers} sub="Total across lines" icon={Users} tone="blue" prev={ky.stitchers} prevLabel={cmpLabel} trendTestid="kpi-trend-stitchers" />
        <Kpi testid="kpi-helpers" label="Table Ops & Helpers" value={k.helpers} sub="On floor" icon={Users} tone="amber" prev={ky.helpers} prevLabel={cmpLabel} trendTestid="kpi-trend-helpers" />
        <Kpi testid="kpi-qc" label="QC" value={k.qc} sub="Quality inspectors" icon={ShieldAlert} tone="slate" prev={ky.qc} prevLabel={cmpLabel} trendTestid="kpi-trend-qc" />
        <Kpi testid="kpi-output" label="Output Today" value={k.output_today.toLocaleString()} sub="Pieces produced" icon={Package} tone="emerald" prev={ky.output} prevLabel={cmpLabel} trendTestid="kpi-trend-output" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-5 border-slate-200 rounded-md bg-white col-span-1 md:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-heading font-semibold text-[15px]">Output — Last 7 days</div>
              <div className="text-[12px] text-slate-500">Daily pieces produced across all lines · grey dotted points are holidays/Sundays, not production drops</div>
            </div>
            <TrendingUp className="h-4 w-4 text-slate-400" />
          </div>
          <div className="h-60 -ml-2">
            <ResponsiveContainer>
              <LineChart data={data.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(d) => d?.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip content={({ active, payload, label }) => active && payload?.length ? (
                  <div className="bg-white border border-slate-200 rounded-md px-3 py-2 text-[12px] shadow-sm">
                    <div className="font-mono text-slate-500">{label}</div>
                    <div className="font-semibold text-slate-900">{Number(payload[0].value).toLocaleString()} pcs</div>
                    {payload[0].payload.off_day && (
                      <div className="text-slate-500 mt-0.5">Holiday · {payload[0].payload.off_label || "non-working day"}</div>
                    )}
                  </div>
                ) : null} />
                <Line type="monotone" dataKey="output" stroke="hsl(343 61% 40%)" strokeWidth={2.2}
                  dot={(props) => {
                    const { cx, cy, payload } = props;
                    return payload?.off_day
                      ? <circle key={payload.date} cx={cx} cy={cy} r={4.5} fill="#f1f5f9" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="2 2" />
                      : <circle key={payload?.date || cx} cx={cx} cy={cy} r={3} fill="hsl(343 61% 40%)" />;
                  }}
                  activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border-slate-200 rounded-md bg-white col-span-1 md:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-heading font-semibold text-[15px]">Line-wise Output — Today vs {cmpLabel === "yesterday" ? "Yesterday" : cmpLabel}</div>
              <div className="text-[12px] text-slate-500">Pieces produced by production line{data.comparison?.is_yesterday === false ? " · previous day was a holiday — compared with the last working day" : ""}</div>
            </div>
          </div>
          <div className="h-60 -ml-2">
            <ResponsiveContainer>
              <BarChart data={data.line_summary}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="line" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e2e8f0" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="output_y" name={cmpLabel === "yesterday" ? "Yesterday" : cmpLabel} fill="#cbd5e1" radius={[4, 4, 0, 0]} />
                <Bar dataKey="output" name="Today" fill="hsl(343 61% 40%)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Pending queues */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
          <div className="p-4 flex items-center justify-between border-b border-slate-100">
            <div>
              <div className="font-heading font-semibold text-[15px]">Recent Enquiries</div>
              <div className="text-[12px] text-slate-500">{k.enquiries_pending} pending / {k.enquiries_total} total</div>
            </div>
            <button
              onClick={() => nav("/enquiries")}
              data-testid="dashboard-view-enquiries"
              className="text-[12.5px] text-brand font-semibold hover:underline inline-flex items-center gap-1"
            >
              View all <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="divide-y divide-slate-100">
            {enquiries.map((e) => (
              <button
                key={e.id}
                onClick={() => nav(`/enquiries/${e.id}`)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-left transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-semibold text-slate-900 truncate">{e.enquiry_number} · {e.buyer}</div>
                  <div className="text-[12px] text-slate-500 truncate">{e.buyer_style} — {e.email_subject}</div>
                </div>
                <StatusChip status={e.status} size="sm" />
              </button>
            ))}
            {enquiries.length === 0 && <div className="p-4 text-sm text-slate-500">No enquiries yet.</div>}
          </div>
        </Card>

        <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
          <div className="p-4 flex items-center justify-between border-b border-slate-100">
            <div>
              <div className="font-heading font-semibold text-[15px]">Operation Bulletins</div>
              <div className="text-[12px] text-slate-500">{k.bulletins_pending} pending / {k.bulletins_total} total</div>
            </div>
            <button
              onClick={() => nav("/bulletins")}
              data-testid="dashboard-view-bulletins"
              className="text-[12.5px] text-brand font-semibold hover:underline inline-flex items-center gap-1"
            >
              View all <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="divide-y divide-slate-100">
            {bulletins.map((b) => (
              <button
                key={b.id}
                onClick={() => nav(`/bulletins/${b.id}`)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-left transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-semibold text-slate-900 truncate">{b.our_style} · {b.buyer}</div>
                  <div className="text-[12px] text-slate-500 truncate">v{b.version} · ₹ {b.total_cost.toFixed(2)}</div>
                </div>
                <StatusChip status={b.status} size="sm" />
              </button>
            ))}
            {bulletins.length === 0 && <div className="p-4 text-sm text-slate-500">No bulletins yet.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
